/**
 * Report a user or a piece of content.
 *
 * Calls POST /api/workflows/moderation/report. Paired with useBlockUser — App Store Guideline 1.2
 * requires BOTH reporting and blocking, and the app previously only had the second.
 *
 * The endpoint can block as part of the same call (`also_block`), which is why this hook
 * invalidates the connection queries useBlockUser invalidates: after a report-and-block the
 * partner lists are stale in exactly the same way.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export type ReportReason =
  | 'harassment'
  | 'spam'
  | 'inappropriate_content'
  | 'impersonation'
  | 'fraud_or_scam'
  | 'other';

export type ReportContentType = 'user' | 'message' | 'referral' | 'profile';

export interface ReportContentRequest {
  reported_user_id: string;
  reason: ReportReason;
  content_type?: ReportContentType;
  content_id?: string | null;
  details?: string | null;
  also_block?: boolean;
}

export interface ReportContentResponse {
  success: boolean;
  /** True when an identical report was already filed in the last few minutes; still a success. */
  duplicate?: boolean;
  message: string;
  data?: {
    reportId: string;
    alsoBlocked: boolean;
  };
}

/** The labels shown in the report form. Kept next to the codes so the two cannot drift. */
export const REPORT_REASONS: Array<{ value: ReportReason; label: string; hint: string }> = [
  {
    value: 'harassment',
    label: 'Harassment or bullying',
    hint: 'Threats, abuse, or repeated unwanted contact'
  },
  {
    value: 'spam',
    label: 'Spam',
    hint: 'Unsolicited advertising or repeated irrelevant messages'
  },
  {
    value: 'inappropriate_content',
    label: 'Inappropriate content',
    hint: 'Offensive, explicit, or otherwise objectionable material'
  },
  {
    value: 'impersonation',
    label: 'Impersonation',
    hint: 'Pretending to be another person or business'
  },
  {
    value: 'fraud_or_scam',
    label: 'Fraud or scam',
    hint: 'Fake jobs, payment scams, or misleading offers'
  },
  { value: 'other', label: 'Something else', hint: 'Tell us what is wrong' }
];

export const useReportContent = () => {
  const queryClient = useQueryClient();

  return useMutation<ReportContentResponse, Error, ReportContentRequest>({
    mutationFn: async (data: ReportContentRequest) => {
      const response = await axios.post(`${API_URL}/api/workflows/moderation/report`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    },
    onSuccess: async (result) => {
      // Only the also-block path changes connection state; a plain report changes nothing the
      // user can see, so there is nothing to refetch.
      if (result.data?.alsoBlocked) {
        await queryClient.refetchQueries({ queryKey: ['/api/workflows/connections'] });
        queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections/pending'] });
        queryClient.invalidateQueries({ queryKey: ['/api/partners/suggested'] });
      }
    }
  });
};
